import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import Marquee from "@/components/Marquee";
import { usePageMeta } from "@/lib/meta";

const CHAPTERS = [
  {
    num: "01",
    title: "Youth sports run on volunteers",
    body: [
      "Youth baseball and softball run on volunteers. They're parents, grandparents, former players, and community members who step up to coach—not because they have unlimited time, but because they care about giving kids a great experience and helping them develop as players and teammates.",
      "But coaching takes time. Practice plans get built after the kids go to bed. Lineups get shuffled in the parking lot. Batting orders get rewritten minutes before first pitch. Notes are scattered across phones, notebooks, text messages, and spreadsheets. Too often, the administrative side of coaching steals time from the reason coaches volunteered in the first place: the players.",
    ],
  },
  {
    num: "02",
    title: "CoachER exists to change that",
    body: [
      "Our mission is to help organize youth baseball and softball one practice, one lineup, and one batting order at a time.",
      "We're building CoachER to take the last-minute panic out of preparing for practices and games. By giving coaches one place to plan, organize, communicate, and make better use of the information they already have, we want to make coaching simpler and more manageable.",
    ],
  },
  {
    num: "03",
    title: "What that means on the field",
    body: [
      "Because when a coach spends less time figuring out what comes next, they can spend more time thinking about what each player needs next.",
      "That means more intentional practices. More organized game days. More opportunities for every player to develop. And fewer late nights spent building tomorrow's plan from scratch.",
      "We understand that most youth sports organizations depend on volunteers with jobs, families, and already-busy schedules. CoachER isn't designed to replace the coach. It's designed to support the coach—giving them practical tools that help turn limited preparation time into a better experience on the field.",
    ],
  },
  {
    num: "04",
    title: "Our goal is simple",
    body: [
      "Give coaches time back. Help them feel prepared. Keep the focus on player development.",
      "From the first practice of the season to the final lineup card, CoachER is here to make the work behind the game a little easier—so volunteer coaches can spend more of their time doing what matters most:",
    ],
  },
];

const Mission = () => {
  usePageMeta(
    "Our Mission | CoachER",
    "Better prepared coaches. Better experiences for players. CoachER organizes youth baseball and softball one practice, one lineup, and one batting order at a time.",
    "/mission"
  );

  return (
    <div data-testid="mission-page">
      <section className="relative overflow-hidden bg-coacher-navy pt-32 pb-20 sm:pt-44 sm:pb-28">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-white/70">
            <span className="inline-block h-px w-10 bg-coacher-red" aria-hidden="true" />
            Our Mission
          </p>
          <MaskedLines
            lines={["Better prepared coaches.", "Better experiences", "for players."]}
            delay={0.15}
            className="mt-6 max-w-4xl font-display text-4xl sm:text-6xl lg:text-7xl font-semibold uppercase leading-[1.0] tracking-tight text-white"
            lineClassName="[&:nth-child(3)]:text-outline-white"
          />
        </div>
      </section>

      <Marquee items={["One practice at a time", "One lineup at a time", "One batting order at a time"]} />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-4xl px-4 sm:px-8">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.num} className="border-t border-coacher-border py-14 first:border-t-0 first:pt-0 sm:py-16">
              <div data-testid={`mission-chapter-${ch.num}`} className="grid gap-6 sm:grid-cols-[auto_1fr] sm:gap-12">
                <span className="font-varsity text-6xl sm:text-7xl leading-none text-outline-navy select-none">{ch.num}</span>
                <div>
                  <h2 className="font-display text-2xl sm:text-3xl font-semibold uppercase tracking-tight text-coacher-navy">{ch.title}</h2>
                  {ch.body.map((p, j) => (
                    <p key={j} className="mt-5 text-base sm:text-lg leading-relaxed text-coacher-slate">{p}</p>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}

          <Reveal className="border-t border-coacher-border pt-16 text-center">
            <p className="font-varsity text-5xl sm:text-7xl text-coacher-navy">Coaching kids.</p>
            <div className="stitch-x mx-auto mt-8 w-40" aria-hidden="true" />
          </Reveal>
        </div>
      </section>

      <section className="relative overflow-hidden bg-coacher-navy py-20 sm:py-24">
        <div className="absolute inset-0 pinstripes-dark" aria-hidden="true" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-8">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-5xl font-semibold uppercase tracking-tight text-white">
              Spend your nights on the field,<br className="hidden sm:block" /> not on the paperwork.
            </h2>
            <Link
              to="/pricing"
              data-testid="mission-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-md bg-coacher-red px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-coacher-red-dark"
            >
              Start Your Free Trial <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Mission;
